import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { GET_SIGNAL_BUSINESS_HOURS_QUERY } from 'src/graphql/entities/signal-business-hours/signal-business-hours.operations';
import {
  SignalBusinessHoursEntity,
  SignalBusinessHoursQueryResponse,
  SignalUrgencyLevel,
} from 'src/graphql/entities/signal-business-hours/signal-business-hours.types';
import { QueryVariables, Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { toEnum } from 'src/utils/enum';
import { useNotifications } from 'src/utils/hooks';

export type GetSignalBusinessHoursQuery = {
  getSignalBusinessHours: Records<SignalBusinessHoursQueryResponse>;
};

export const useSignalBusinessHours = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const { data, loading: isLoading } = useQuery<GetSignalBusinessHoursQuery, QueryVariables>(
    GET_SIGNAL_BUSINESS_HOURS_QUERY,
    {
      variables: {
        params: {
          filters: [
            {
              attribute: 'partnerId',
              operator: '=',
              value: selectedPartnerId?.toString()!,
            },
          ],
        },
      },
      skip: selectedPartnerId === null,
      onError: (error) => {
        console.error(error);
        showErrorAlert('Failed to query Signal business hours, please try again');
      },
    },
  );

  const records = useMemo<SignalBusinessHoursEntity[]>(
    () =>
      data?.getSignalBusinessHours.records.map<SignalBusinessHoursEntity>((record) => ({
        ...record,
        timeFrom: record.timeFrom,
        timeTo: record.timeTo,
        urgency: toEnum(SignalUrgencyLevel, record.urgency)!,
      })) || [],
    [data?.getSignalBusinessHours.records],
  );

  return {
    signalBusinessHours: records,
    isLoading,
  };
};
